export type Accent =
  | "amber"
  | "emerald"
  | "sky"
  | "violet"
  | "rose"
  | "slate"
  | "lime"
  | "orange";

// ---- Snapshot (Marktpreise tab) -------------------------------------------

export interface DashboardCell {
  label: string;
  contractName: string | null;
  bid: number | null;
  ask: number | null;
  last: number | null;
  prevClose: number | null;
  change: number | null;
  updateTime: string | null;
  /** Delivery day (spot) or trading day (futures), ISO yyyy-MM-dd; shown in the "Datum" column. */
  priceDate: string | null;
  error?: string;
}

export interface DashboardSection {
  id: string;
  title: string;
  unit: string;
  accent: Accent;
  rows: DashboardCell[];
}

export interface DashboardSnapshot {
  updatedAt: string;
  cached: boolean;
  sections: DashboardSection[];
}

export async function fetchSnapshot(force = false): Promise<DashboardSnapshot> {
  const qs = force ? "?refresh=true" : "";
  const res = await fetch(`/api/dashboard/snapshot${qs}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Snapshot ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

// ---- QPFC (PFC tab) -------------------------------------------------------

export interface PfcDailyPoint {
  date: string; // yyyy-MM-dd (Europe/Berlin)
  value: number;
}

export type PfcCommodity = "power" | "gas";

export interface PfcLevels {
  base: PfcDailyPoint[];
  peak?: PfcDailyPoint[];
}

export interface PfcPayload {
  commodity: PfcCommodity;
  tradeDate: string; // settlement date used (last trading day)
  updatedAt: string;
  minYear: number;
  maxYear: number;
  products: number;
  unit: string;
  daily: PfcDailyPoint[];
  /** Finest underlying settlement prices as step levels (Base, and Peak for power). */
  levels: PfcLevels;
}

export async function fetchPfc(commodity: PfcCommodity, force = false): Promise<PfcPayload> {
  const params = new URLSearchParams({ commodity });
  if (force) params.set("refresh", "true");
  const res = await fetch(`/api/dashboard/qpfc?${params}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PFC ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/** Download the PFC as a PRICEIT-style CSV for the chosen commodity + year range. */
export async function downloadPfcCsv(
  commodity: PfcCommodity,
  fromYear: number,
  toYear: number,
): Promise<void> {
  const params = new URLSearchParams({
    commodity,
    format: "csv",
    fromYear: String(fromYear),
    toYear: String(toYear),
  });
  const res = await fetch(`/api/dashboard/qpfc?${params}`, {
    credentials: "include",
    headers: { Accept: "text/csv" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`CSV ${res.status}: ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ?? `PFC_${commodity}_${fromYear}-${toYear}.csv`;
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

// ---- Uploaded PFCs (PFCs tab) ---------------------------------------------

export type PfcGranularity = "quarter_hour" | "hourly" | "daily";

export interface PfcWhoami {
  email: string | null;
  sub: string | null;
  isSuperadmin: boolean;
}

export interface PfcCurveSummary {
  curveKey: string;
  versionCount: number;
  latestPriceDate: string;
  latestUploadedAt: string;
  granularity: PfcGranularity;
  latestId: number;
}

export interface PfcVersion {
  id: number;
  curveKey: string;
  priceDate: string;
  granularity: PfcGranularity;
  uploadedAt: string;
  uploadedBy: string;
  startTs: string;
  endTs: string;
  pointCount: number;
  origFilename: string | null;
  sourceNote: string | null;
  unit: string | null;
}

export interface PfcPoint {
  ts: number;
  value: number;
}

export interface PfcPointsResponse {
  id: number;
  points: PfcPoint[];
  hasMore: boolean;
  nextFromMs: number | null;
}

async function jsonGet<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

export function fetchPfcWhoami(): Promise<PfcWhoami> {
  return jsonGet("/api/pfc/whoami");
}

export async function fetchPfcCurves(): Promise<PfcCurveSummary[]> {
  const r = await jsonGet<{ curves: PfcCurveSummary[] }>("/api/pfc/curves");
  return r.curves;
}

export async function fetchPfcVersions(curveKey: string): Promise<PfcVersion[]> {
  const r = await jsonGet<{ curveKey: string; versions: PfcVersion[] }>(
    `/api/pfc/curves/${encodeURIComponent(curveKey)}/versions`,
  );
  return r.versions;
}

export async function fetchPfcPoints(
  id: number,
  opts?: { from?: string; to?: string; limit?: number },
): Promise<PfcPointsResponse> {
  const params = new URLSearchParams();
  if (opts?.from) params.set("from", opts.from);
  if (opts?.to) params.set("to", opts.to);
  if (opts?.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return jsonGet<PfcPointsResponse>(`/api/pfc/${id}/points${qs ? `?${qs}` : ""}`);
}

/**
 * Delete one uploaded PFC version. Superadmin-only on the worker side —
 * a 403 is surfaced as a thrown Error so the UI can show a friendly hint.
 * The Worker also removes the R2 mirror best-effort under waitUntil.
 */
export async function deletePfc(id: number): Promise<PfcVersion> {
  const res = await fetch(`/api/pfc/${id}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let msg = text;
    try {
      const j = JSON.parse(text) as { error?: string };
      msg = j.error ?? text;
    } catch {
      /* keep */
    }
    throw new Error(`${res.status}: ${msg || res.statusText}`);
  }
  const j = (await res.json()) as { deleted: PfcVersion };
  return j.deleted;
}

/**
 * Upload a PRICEIT CSV via the superadmin endpoint. Returns the newly
 * created PFC metadata. The Worker parses the filename and body — the
 * SPA just wires them into a multipart form. A 403 from the endpoint is
 * surfaced as an Error so the UI can show a "nur für Administratoren"
 * hint without needing a whoami round-trip beforehand.
 */
export async function uploadPfc(file: File, sourceNote?: string): Promise<PfcVersion> {
  const form = new FormData();
  form.append("file", file);
  if (sourceNote?.trim()) form.append("source_note", sourceNote.trim());
  // Posts to /api/pfc/upload (not /admin/pfc/upload) — the dashboard
  // subdomain's Worker route only forwards /api/* to this worker; /admin/*
  // there lands on Cloudflare Pages and 405s. The endpoint is still
  // superadmin-gated on the worker side.
  const res = await fetch("/api/pfc/upload", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let msg = text;
    try {
      const j = JSON.parse(text) as { error?: string };
      msg = j.error ?? text;
    } catch {
      /* keep text */
    }
    throw new Error(`${res.status}: ${msg || res.statusText}`);
  }
  const j = (await res.json()) as { pfc: PfcVersion };
  return j.pfc;
}

// ---- Futures history (Overview tabs) --------------------------------------

export interface FuturesHistoryPoint {
  date: string;
  settlement: number | null;
}

export type ContractTenor = "month" | "quarter" | "year";

export interface FuturesHistorySeries {
  /** Stable id = real contract name, e.g. "Cal-2027". */
  id: string;
  /** Display label, identical to the id ("Cal-2027" / "Q3-2026" / "Jul-2026"). */
  contractName: string;
  /** Tenor bucket — drives legend grouping + colour family. */
  tenor: ContractTenor;
  /** Delivery-period start epoch (ms) — for chronological ordering. */
  deliveryStartMs: number;
  /** Visible without a user toggle (true for year contracts). */
  defaultVisible: boolean;
  points: FuturesHistoryPoint[];
  latestSettlement: number | null;
}

export interface FuturesHistoryCurve {
  id: "power-base" | "power-peak" | "gas";
  title: string;
  marketPrefix: string;
  accent: Accent;
  series: FuturesHistorySeries[];
}

export interface FuturesHistoryPayload {
  updatedAt: string;
  cached: boolean;
  windowDays: number;
  curves: FuturesHistoryCurve[];
}

export async function fetchFuturesHistory(force = false): Promise<FuturesHistoryPayload> {
  return getJson<FuturesHistoryPayload>("/api/dashboard/futures-history", force);
}

// ---- Power spot series (15-min + daily + forecasts) -----------------------

export interface PowerSpotPoint {
  ts: string;
  value: number | null;
}

export interface PowerSpotDaily {
  date: string;
  base: number | null;
  peak: number | null;
}

export interface PowerSpotSeriesPayload {
  updatedAt: string;
  cached: boolean;
  now: string;
  quarters: PowerSpotPoint[];
  daily: PowerSpotDaily[];
  forecastEc00: PowerSpotPoint[];
  forecastEc00PriceDrop: PowerSpotPoint[];
  errors: Partial<Record<"quarters" | "daily" | "forecastEc00" | "forecastEc00PriceDrop", string>>;
}

export async function fetchPowerSpotSeries(force = false): Promise<PowerSpotSeriesPayload> {
  return getJson<PowerSpotSeriesPayload>("/api/dashboard/power-spot-series", force);
}

// ---- Gas spot series (EGSI + D0..D7 forwards) -----------------------------

export interface GasForwardQuote {
  frontSymbol: string;
  code: string;
  settlement: number | null;
  prevSettlement: number | null;
}

export interface GasSpotSeriesPayload {
  updatedAt: string;
  cached: boolean;
  now: string;
  egsi: FuturesHistoryPoint[];
  forwards: GasForwardQuote[];
  errors: Partial<Record<"egsi" | "forwards", string>>;
}

export async function fetchGasSpotSeries(force = false): Promise<GasSpotSeriesPayload> {
  return getJson<GasSpotSeriesPayload>("/api/dashboard/gas-spot-series", force);
}

// ---- Gas storage (DE) seasonal chart --------------------------------------

export interface GasStorageBandPoint {
  doy: number;
  monthDay: string;
  min: number | null;
  max: number | null;
}

export interface GasStorageYearPoint {
  date: string;
  monthDay: string;
  doy: number;
  pct: number | null;
  inv: number | null;
  cap: number | null;
}

export interface GasStorageSummary {
  date: string;
  pct: number | null;
  inv: number | null;
  cap: number | null;
}

export interface GasStoragePayload {
  updatedAt: string;
  cached: boolean;
  currentYearLabel: number;
  bandYears: number[];
  current: GasStorageSummary;
  yearAgo: GasStorageSummary;
  band: GasStorageBandPoint[];
  currentYear: GasStorageYearPoint[];
  prevYear: GasStorageYearPoint[];
  errors: Partial<Record<"inventory" | "capacity", string>>;
}

export async function fetchGasStorage(force = false): Promise<GasStoragePayload> {
  return getJson<GasStoragePayload>("/api/dashboard/gas-storage", force);
}

// ---- Power generation (DE Wind/Solar) -------------------------------------

export type PowerGenLatestKey = "windOnshore" | "windOffshore" | "solar";
export type PowerGenSeriesKey = "wind" | "solar";

export interface PowerGenPoint {
  ts: string;
  value: number | null;
}

export interface PowerGenSeries {
  source: PowerGenSeriesKey;
  kind: "realized" | "forecast";
  points: PowerGenPoint[];
  error?: string;
}

export interface PowerGenerationPayload {
  updatedAt: string;
  cached: boolean;
  now: string;
  series: PowerGenSeries[];
  latest: Partial<Record<PowerGenLatestKey, { ts: string; value: number }>>;
  errors: Partial<Record<string, string>>;
}

export async function fetchPowerGeneration(force = false): Promise<PowerGenerationPayload> {
  return getJson<PowerGenerationPayload>("/api/dashboard/power-generation", force);
}

// ---- Feature request ------------------------------------------------------

export interface FeatureRequestResponse {
  ok: boolean;
  issueNumber?: number;
  issueUrl?: string;
}

export async function submitFeatureRequest(input: {
  title: string;
  body: string;
}): Promise<FeatureRequestResponse> {
  const res = await fetch("/api/dashboard/feature-request", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(input),
  });
  const body = (await res.json().catch(() => ({}))) as
    | FeatureRequestResponse
    | { error?: string; detail?: string };
  if (!res.ok) {
    const msg = "error" in body && body.error ? body.error : `Server ${res.status}`;
    const detail = "detail" in body && body.detail ? ` (${body.detail})` : "";
    throw new Error(msg + detail);
  }
  return body as FeatureRequestResponse;
}

// ---- low-level fetch helper -----------------------------------------------

async function getJson<T>(path: string, force: boolean): Promise<T> {
  const qs = force ? "?refresh=true" : "";
  const res = await fetch(`${path}${qs}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${path} ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}
